import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Modal } from '../ui/Modal';
import { useApp } from '../../context/AppContext';
import { parseISO, format, startOfDay } from 'date-fns';
import apiService from '../../services/api';
import {
  Umbrella, Home, Heart, Baby, DollarSign, Thermometer,
  Tag, Cake, Users, Stethoscope, GraduationCap,
  Briefcase, Globe, RefreshCw, Gift, ChevronDown, Check,
  AlertTriangle, Loader2, CalendarDays, Sunrise, Sunset, Clock, Flag, Upload, FileText, Info, UserCheck, ShieldAlert,
} from 'lucide-react';
import { DateRangePicker } from './DateRangePicker';
import { holidaysForEmp, isNonWorkingDay, normaliseSchedules, unwrap } from '../../utils/leaveBalance';
import { fmtRange, fmtAmount, fmtDayCount, fmtHourCount } from '../../utils/format';

// ─────────────────────────────────────────────────────────────
// Booking leave. Everything shown before Submit comes from
// POST /leave-requests/preview, the same calculation the server enforces:
// working days (non-working days and public holidays left out), the balance
// before and after, the company's booking rules, colleagues already off,
// whether a sick note is needed and who will approve it. Submit is blocked
// when the booking would overdraw the balance (approvers booking for someone
// else can choose to go over, and it's recorded).
// ─────────────────────────────────────────────────────────────

const WORK_START = '09:00';
const WORK_END   = '17:30';

function generateTimeOptions() {
  const options = [];
  let h = 9, m = 0;
  while (h < 17 || (h === 17 && m <= 30)) {
    options.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    m += 15;
    if (m >= 60) { m = 0; h++; }
  }
  return options;
}
const TIME_OPTIONS = generateTimeOptions();
const parseHHMM = (str) => { if (!str) return null; const [h, m] = str.split(':').map(Number); return h * 60 + m; };
function calcCustomHours(start, end) {
  const s = parseHHMM(start), e = parseHHMM(end);
  if (s == null || e == null || e <= s) return 0;
  return Math.round(((e - s) / 60) * 100) / 100;
}

// Line icons (no emoji), the same set as the rest of the app
const PERIOD_OPTIONS = [
  { value: 'full',   label: 'Full days',    sublabel: 'One or more days', Icon: CalendarDays },
  { value: 'AM',     label: 'Morning',      sublabel: 'Half day',          Icon: Sunrise },
  { value: 'PM',     label: 'Afternoon',    sublabel: 'Half day',          Icon: Sunset },
  { value: 'custom', label: 'Custom hours', sublabel: 'Pick start and end', Icon: Clock },
];

const ICON_MAP = {
  'Annual Leave': Umbrella, 'Holiday': Umbrella,
  'Sick': Thermometer, 'Sickness': Thermometer, "Personal/Carer": Thermometer,
  'Remote': Home, 'From Home': Home, 'Abroad': Globe,
  'Unpaid': DollarSign, 'Compassionate': Heart, 'Family Responsibility': Heart,
  'Parental': Baby, 'Maternity': Baby, 'Paternity': Baby,
  'Medical': Stethoscope, 'Meeting': Users, 'Training': GraduationCap, 'Study': GraduationCap,
  'Birthday': Cake, 'Christmas': Gift, 'Day Swap': RefreshCw, 'Working': Briefcase,
};
function getIcon(name) {
  if (!name) return Tag;
  const match = Object.entries(ICON_MAP).find(([key]) => name.toLowerCase().includes(key.toLowerCase()));
  return match?.[1] || Tag;
}

// ─── Leave type dropdown ─────────────────────────────────────
function LeaveTypeDropdown({ leaveTypes, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const deductible    = leaveTypes.filter(t =>  t.affectsBalance);
  const nonDeductible = leaveTypes.filter(t => !t.affectsBalance);
  const selected      = leaveTypes.find(t => t.name === value);
  const SelectedIcon  = getIcon(selected?.name);

  useEffect(() => {
    const handle = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const item = (type) => {
    const isSel = type.name === value;
    const Icon = getIcon(type.name);
    return (
      <button
        type="button"
        key={type.id ?? type.name}
        role="option"
        aria-selected={isSel}
        onClick={() => { onChange(type.name); setOpen(false); }}
        className="rf-opt"
        style={{ background: isSel ? 'var(--accent)' : undefined }}
      >
        <span className="rf-opt-ic" style={{ background: isSel ? 'var(--on-accent-soft)' : (type.color || '#ffc81e') }}>
          <Icon size={14} style={{ color: isSel ? 'var(--on-accent)' : '#1C1A14' }} strokeWidth={2.4} />
        </span>
        <span style={{ fontSize: 13, fontWeight: 500, color: isSel ? 'var(--on-accent)' : 'var(--text)', flex: 1, textAlign: 'left' }}>{type.name}</span>
        {isSel && <Check size={14} style={{ color: 'var(--on-accent)' }} />}
      </button>
    );
  };
  const group = (label, types) => types.length ? (
    <>
      <div className="rf-group">{label}</div>
      {types.map(item)}
    </>
  ) : null;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} className="rf-select" style={{ borderColor: open ? 'var(--accent)' : undefined }}>
        {selected ? (
          <>
            <span className="rf-opt-ic" style={{ background: selected.color || '#ffc81e' }}><SelectedIcon size={14} color="#1C1A14" strokeWidth={2.4} /></span>
            <span style={{ flex: 1, textAlign: 'left', fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{selected.name}</span>
          </>
        ) : <span style={{ flex: 1, textAlign: 'left', fontSize: 13, color: 'var(--text-muted)' }}>Choose a leave type…</span>}
        <ChevronDown size={15} style={{ color: 'var(--text-muted)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>
      {open && (
        <div role="listbox" className="rf-menu">
          {group('Comes off your allowance', deductible)}
          {deductible.length > 0 && nonDeductible.length > 0 && <div style={{ borderTop: '1px solid var(--border-soft)', margin: '4px 0' }} />}
          {group('Doesn’t use your annual allowance', nonDeductible)}
        </div>
      )}
    </div>
  );
}

// Unpaid break on a custom-hours booking (minutes)
const BREAK_OPTIONS = [0, 15, 30, 45, 60, 90];
const fmtBreak = (m) => (m >= 60 && m % 60 === 0 ? `${m / 60} hour` : m > 60 ? `${Math.floor(m / 60)}h ${m % 60} min` : `${m} min`);
function BreakSelect({ value, onChange, spanMinutes }) {
  const opts = [...new Set([...BREAK_OPTIONS, Number(value) || 0])].sort((a, b) => a - b)
    .filter(m => m === 0 || !spanMinutes || m < spanMinutes);
  return (
    <label style={{ flex: 1, minWidth: 120 }}>
      <span className="rf-label">Unpaid break</span>
      <select className="form-input" value={String(Number(value) || 0)} onChange={e => onChange(Number(e.target.value))}>
        {opts.map(m => <option key={m} value={m}>{m === 0 ? 'No break' : fmtBreak(m)}</option>)}
      </select>
    </label>
  );
}

function TimeSelect({ label, value, onChange, min, max }) {
  const opts = TIME_OPTIONS.filter(t => (!min || parseHHMM(t) > parseHHMM(min)) && (!max || parseHHMM(t) <= parseHHMM(max)));
  return (
    <label style={{ flex: 1, minWidth: 120 }}>
      <span className="rf-label">{label}</span>
      <select className="form-input" value={value} onChange={e => onChange(e.target.value)}>
        <option value="">-- : --</option>
        {opts.map(t => <option key={t} value={t}>{t}</option>)}
      </select>
    </label>
  );
}

// ─── The form ────────────────────────────────────────────────
export function RequestForm({ onClose, prefill = null, onRequestCreated }) {
  const { currentUser, employees, requests: allRequests, isAdmin, approvalScope, holidayWord, notifyLeaveChanged } = useApp();

  // Approvers can book for the people they approve ("book on behalf of")
  const bookable = useMemo(() => {
    if (!currentUser) return [];
    const ids = isAdmin ? null : new Set(approvalScope?.userIds ?? []);
    return employees
      .filter(e => e.id !== currentUser.id && (e.status ?? 'active') === 'active' && (!ids || ids.has(e.id)))
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  }, [employees, currentUser, isAdmin, approvalScope]);

  const [empId, setEmpId] = useState(prefill?.empId ?? currentUser?.id);
  const emp = employees.find(e => e.id === empId) ?? (empId === currentUser?.id ? currentUser : null);
  const empName = prefill?.empName ?? emp?.name ?? `${emp?.firstName ?? ''} ${emp?.lastName ?? ''}`.trim();
  const isForOther = empId !== currentUser?.id;

  const [leaveTypes, setLeaveTypes] = useState([]);
  const [schedules,  setSchedules]  = useState([]);
  const [holidays,   setHolidays]   = useState([]);
  const [form, setForm] = useState({
    type: '', from: prefill?.from ?? '', to: prefill?.to ?? prefill?.from ?? '', note: '',
    period: prefill?.halfDayPeriod ?? prefill?.period ?? 'full', startTime: '', endTime: '',
    breakMinutes: '', // '' = the company's usual break (decided by the server)
    parentalKind: 'birth', parentalDate: '',
  });
  const [preview,    setPreview]    = useState(null);
  const [previewErr, setPreviewErr] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [overdrawOk, setOverdrawOk] = useState(false);
  const [docFile,    setDocFile]    = useState(null);
  const [error,      setError]      = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done,       setDone]       = useState(null);

  const isHalfDay = form.period === 'AM' || form.period === 'PM';
  const isCustom  = form.period === 'custom';
  const single    = isHalfDay || isCustom;
  const customHours = isCustom ? calcCustomHours(form.startTime, form.endTime) : 0;
  const selectedType = leaveTypes.find(t => t.name === form.type);
  const isPrivateType = !!selectedType?.isPrivate || /sick|medical|carer|parental|maternity|paternity|family/i.test(form.type);
  const today = startOfDay(new Date());

  // Leave types
  useEffect(() => {
    apiService.getLeaveTypes().then(res => {
      const active = (unwrap(res) ?? []).filter(t => t.isActive !== false);
      const sorted = [...active].sort((a, b) => (b.affectsBalance ? 1 : 0) - (a.affectsBalance ? 1 : 0) || (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      setLeaveTypes(sorted);
      setForm(f => ({ ...f, type: f.type || sorted[0]?.name || '' }));
    }).catch(() => {});
  }, []);

  // The person's schedule and their holiday location's public holidays
  useEffect(() => {
    if (!empId) return;
    const y = new Date().getFullYear();
    Promise.all([
      apiService.getEmployeeSchedules(empId).catch(() => null),
      apiService.getEmployeeAllowance(empId, y).catch(() => null),
      apiService.getPublicHolidays(y).catch(() => null),
      apiService.getPublicHolidays(y + 1).catch(() => null),
    ]).then(([sch, allowance, h1, h2]) => {
      setSchedules(normaliseSchedules(sch));
      const all = [...(unwrap(h1) ?? []), ...(unwrap(h2) ?? [])];
      const a = unwrap(allowance);
      setHolidays(a?.observesPublicHolidays === false ? [] : holidaysForEmp(all, a, empId));
    });
  }, [empId]);

  // Colleagues in the same department already off (for the calendar dots)
  const deptIdsOf = (e) => new Set([
    e?.departmentId, ...(e?.departments ?? []).map(d => d.departmentId ?? d.id),
  ].filter(Boolean));
  const myDepts = useMemo(() => deptIdsOf(emp), [emp]); // eslint-disable-line react-hooks/exhaustive-deps
  const colleagueLeave = useMemo(() => {
    if (!myDepts.size) return [];
    const byId = new Map(employees.map(e => [e.id, e]));
    return (allRequests ?? []).filter(r => {
      if (!['approved', 'pending'].includes(r.status) || r.empId === empId) return false;
      if (r.leaveType?.countsAsAbsence === false) return false;
      const other = byId.get(r.empId);
      return other && [...deptIdsOf(other)].some(d => myDepts.has(d));
    }).map(r => ({ from: String(r.from).slice(0, 10), to: String(r.to ?? r.from).slice(0, 10), name: byId.get(r.empId)?.name ?? 'Colleague', label: r.type }));
  }, [allRequests, employees, myDepts, empId]);
  const clashesOn = (d) => { const k = format(d, 'yyyy-MM-dd'); return colleagueLeave.filter(c => c.from <= k && c.to >= k); };
  const isWorkingDay = (d) => (schedules.length ? !isNonWorkingDay(schedules, d) : ![0, 6].includes(d.getDay()));

  // Ask the server what this booking means, as the form changes
  const ready = form.type && form.from && (single || form.to) && (!isCustom || customHours > 0);
  const payload = useMemo(() => ({
    empId,
    type: form.type,
    from: form.from,
    to: single ? form.from : form.to,
    period: form.period,
    startTime: isCustom ? form.startTime : null,
    endTime: isCustom ? form.endTime : null,
    hours: isCustom ? customHours : undefined,
    // Custom hours: unpaid break taken off the time (left out = company default)
    breakMinutes: isCustom && form.breakMinutes !== '' ? Number(form.breakMinutes) : undefined,
    parentalEvent: selectedType?.parentalCap ? { kind: form.parentalKind, date: form.parentalDate || form.from } : undefined,
  }), [empId, form, single, isCustom, customHours, selectedType]);

  useEffect(() => {
    if (!ready) { setPreview(null); setPreviewErr(null); return undefined; }
    let live = true;
    setPreviewing(true);
    const t = setTimeout(() => {
      apiService.previewLeaveRequest(payload)
        .then(res => { if (live) { setPreview(unwrap(res)); setPreviewErr(null); } })
        .catch(e => { if (live) { setPreview(null); setPreviewErr(e?.response?.data?.message || 'Could not check these dates.'); } })
        .finally(() => { if (live) setPreviewing(false); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [ready, payload]);

  useEffect(() => { setOverdrawOk(false); }, [payload]);

  const p = preview;
  const overdraw = !!(p?.balance?.overdraw || p?.typeAllowance?.overdraw);
  const blocked = p?.rules?.blocked || (p?.parental?.exceeds ? { message: `That's more than the ${p.parental.capLabel} of parental leave for this child.` } : null);
  const canSubmit = !!p && !previewing && !blocked && (!overdraw || (p.canOverdraw && overdrawOk)) && !submitting;

  const set = (k, v) => { setError(''); setForm(f => ({ ...f, [k]: v })); };
  function setPeriod(v) {
    setError('');
    setForm(f => ({ ...f, period: v, startTime: v === 'custom' ? (f.startTime || WORK_START) : f.startTime, to: v === 'full' ? f.to : f.from }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await apiService.createLeaveRequest({ ...payload, note: form.note, allowOverdraw: overdraw && overdrawOk ? true : undefined });
      const created = unwrap(res) ?? {};
      let docNote = null;
      if (docFile && created.id) {
        try { await apiService.uploadLeaveDocument(created.id, docFile); docNote = 'uploaded'; }
        catch (upErr) { docNote = upErr?.response?.data?.message || 'The document could not be uploaded. You can add it from Requests.'; }
      }
      const enriched = { ...created, leaveType: created.leaveType ?? selectedType ?? null };
      onRequestCreated?.(enriched, null);
      notifyLeaveChanged?.();
      setDone({ req: enriched, docNote, warnings: res?.warnings ?? [] });
    } catch (apiErr) {
      setError(apiErr?.response?.data?.message || 'Failed to submit the request. Please try again.');
      onRequestCreated?.(null, apiErr);
    } finally {
      setSubmitting(false);
    }
  }

  const styles = (
    <style>{`
      .rf-label { display: block; font-size: 12px; font-weight: 600; color: var(--text-2); margin-bottom: 6px; }
      .rf-select { width: 100%; display: flex; align-items: center; gap: 10px; padding: 9px 12px; border: 1.5px solid var(--field-border); border-radius: 12px; background: var(--field); cursor: pointer; }
      .rf-menu { position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 200; background: var(--menu-bg); border: 1px solid var(--border); border-radius: 12px; box-shadow: var(--menu-shadow); max-height: 320px; overflow-y: auto; padding: 4px 0; }
      .rf-group { padding: 8px 14px 4px; font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.06em; }
      .rf-opt { width: 100%; display: flex; align-items: center; gap: 10px; padding: 8px 14px; border: none; background: transparent; cursor: pointer; }
      .rf-opt:hover { background: var(--hover); }
      .rf-opt-ic { width: 28px; height: 28px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
      .rf-periods { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 6px; }
      @media (max-width: 520px) { .rf-periods { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
      .rf-period { padding: 9px 4px; border-radius: 12px; border: 1px solid var(--field-border); background: var(--field); cursor: pointer; text-align: center; }
      .rf-period[aria-pressed="true"] { border: 2px solid var(--accent); background: var(--tint); }
      .rf-box { border-radius: 12px; border: 1px solid var(--border); background: var(--surface-2); padding: 10px 12px; font-size: 12.5px; color: var(--text-2); }
      .rf-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
      .rf-row + .rf-row { margin-top: 6px; }
      .rf-strong { font-weight: 700; color: var(--text); }
      .rf-bad { border-color: var(--bad-border); background: var(--bad-bg); color: var(--bad-text); }
      .rf-warn { border-color: var(--warn-border); background: var(--warn-bg-soft); color: var(--warn-text); }
      .rf-ok { border-color: var(--ok-border); background: var(--ok-bg); color: var(--ok-text); }
      .rf-chip { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 10px; background: var(--surface); border: 1px solid var(--border); font-size: 11.5px; color: var(--text-2); }
    `}</style>
  );

  // ── Success ────────────────────────────────────────────────
  if (done) {
    const r = done.req;
    const Icon = getIcon(r.type);
    const approved = r.status === 'approved';
    const half = r.period === 'AM' || r.period === 'PM';
    return (
      <Modal title={approved ? 'Leave booked' : 'Request sent'} onClose={onClose}>
        {styles}
        <div style={{ textAlign: 'center', paddingTop: 4 }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--ok-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <Check size={24} style={{ color: 'var(--ok-text)' }} strokeWidth={2.6} />
          </div>
          <p style={{ margin: '0 0 18px', fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.6 }}>
            {approved
              ? (isForOther ? `${empName}'s leave is booked.` : 'Your leave is booked.')
              : `Waiting for ${p?.approver?.name ?? 'your approver'} to approve it.`}
          </p>
          <div className="rf-box" style={{ textAlign: 'left', marginBottom: 14 }}>
            <div className="rf-row" style={{ justifyContent: 'flex-start', gap: 10 }}>
              <span className="rf-opt-ic" style={{ background: selectedType?.color || '#ffc81e', borderRadius: 8 }}><Icon size={15} color="#1C1A14" /></span>
              <span style={{ flex: 1 }}>
                <span className="rf-strong" style={{ display: 'block' }}>{r.type}</span>
                <span>{fmtRange(r.from, r.to)} · {isCustom ? `${r.hours}h` : half ? `½ day (${r.period === 'AM' ? 'morning' : 'afternoon'})` : fmtDayCount(r.days)}</span>
              </span>
              <span className={`rf-chip ${approved ? 'rf-ok' : 'rf-warn'}`}>{approved ? 'Approved' : 'Pending'}</span>
            </div>
            {p?.balance && (
              <div className="rf-row" style={{ marginTop: 10 }}>
                <span>Balance after this</span>
                <span className="rf-strong" style={{ color: p.balance.after < 0 ? 'var(--bad-text)' : undefined }}>{fmtAmount(p.balance.after, p.balance.unit)}</span>
              </div>
            )}
          </div>
          {done.docNote && (
            <p className={`rf-box ${done.docNote === 'uploaded' ? 'rf-ok' : 'rf-warn'}`} style={{ textAlign: 'left', marginBottom: 14 }}>
              {done.docNote === 'uploaded' ? 'Sick note uploaded. Only HR can see it.' : done.docNote}
            </p>
          )}
          {!done.docNote && r.documentRequired && (
            <p className="rf-box rf-warn" style={{ textAlign: 'left', marginBottom: 14 }}>
              A sick note is needed for this absence. Add it from Requests when you have it.
            </p>
          )}
          <button className="btn-primary" onClick={onClose} style={{ width: '100%' }}>Done</button>
        </div>
      </Modal>
    );
  }

  // ── Form ──────────────────────────────────────────────────
  const rangeText = form.from
    ? (single || !form.to ? fmtRange(form.from, form.from) : fmtRange(form.from, form.to))
    : null;

  return (
    <Modal title={isForOther ? `Book leave for ${empName}` : 'New leave request'} onClose={onClose} width="max-w-2xl">
      {styles}
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>

        {/* Who (approvers can book for the people they approve) */}
        {bookable.length > 0 && !prefill?.empId && (
          <label>
            <span className="rf-label">Who is it for?</span>
            <select className="form-input" value={empId} onChange={e => { setEmpId(e.target.value); setPreview(null); }}>
              <option value={currentUser?.id}>Me</option>
              {bookable.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </label>
        )}
        {isForOther && (
          <div className="rf-box" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserCheck size={15} /> Booking for <span className="rf-strong">{empName}</span>. It's approved straight away and they're told.
          </div>
        )}

        {/* Leave type */}
        <div>
          <span className="rf-label">Leave type</span>
          <LeaveTypeDropdown leaveTypes={leaveTypes} value={form.type} onChange={v => set('type', v)} />
          {p?.typeAllowance && (
            <p style={{ margin: '6px 2px 0', fontSize: 12, color: 'var(--text-muted)' }}>
              {fmtDayCount(p.typeAllowance.available)} of {fmtDayCount(p.typeAllowance.entitled)} left in the cycle {fmtRange(p.typeAllowance.cycle.start, p.typeAllowance.cycle.end, { weekday: false })}.
            </p>
          )}
          {selectedType && !selectedType.affectsBalance && !p?.typeAllowance && (
            <p style={{ margin: '6px 2px 0', fontSize: 12, color: 'var(--text-muted)' }}>Doesn't come off the annual leave allowance.</p>
          )}
        </div>

        {/* Duration */}
        <div>
          <span className="rf-label">Duration</span>
          <div className="rf-periods" role="group" aria-label="Duration">
            {PERIOD_OPTIONS.map(({ value, label, sublabel, Icon }) => (
              <button key={value} type="button" className="rf-period" aria-pressed={form.period === value} onClick={() => setPeriod(value)}>
                <Icon size={16} style={{ color: form.period === value ? 'var(--accent)' : 'var(--text-2)', marginBottom: 3 }} />
                <div style={{ fontSize: 12, fontWeight: 700, color: form.period === value ? 'var(--accent)' : 'var(--text)' }}>{label}</div>
                <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>{sublabel}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Dates */}
        <div>
          <div className="rf-row" style={{ marginBottom: 6 }}>
            <span className="rf-label" style={{ margin: 0 }}>{single ? 'Day' : 'Dates'}</span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: form.from ? 'var(--text)' : 'var(--text-muted)' }} aria-live="polite">
              {rangeText ?? (single ? 'Pick a day' : 'Pick the first and last day')}
              {!single && form.from && !form.to && ' – …'}
            </span>
          </div>
          <DateRangePicker
            from={form.from}
            to={single ? form.from : form.to}
            single={single}
            onChange={({ from, to }) => { setError(''); setForm(f => ({ ...f, from, to })); }}
            holidays={holidays}
            isWorkingDay={isWorkingDay}
            clashesOn={clashesOn}
            holidayWord={holidayWord?.() ?? 'Public holiday'}
            minDate={isAdmin ? null : today}
          />
        </div>

        {isCustom && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <TimeSelect label="Start time" value={form.startTime} onChange={v => setForm(f => ({ ...f, startTime: v, endTime: f.endTime && parseHHMM(f.endTime) <= parseHHMM(v) ? '' : f.endTime }))} max={WORK_END} />
            <TimeSelect label="End time" value={form.endTime} onChange={v => set('endTime', v)} min={form.startTime || WORK_START} max={WORK_END} />
            <BreakSelect
              value={form.breakMinutes !== '' ? Number(form.breakMinutes) : (p?.custom?.breakMinutes ?? 0)}
              spanMinutes={customHours * 60}
              onChange={v => set('breakMinutes', String(v))}
            />
          </div>
        )}

        {/* Parental leave: which child event (the cap is per child) */}
        {selectedType?.parentalCap && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ flex: 1, minWidth: 160 }}>
              <span className="rf-label">For</span>
              <select className="form-input" value={form.parentalKind} onChange={e => set('parentalKind', e.target.value)}>
                <option value="birth">A birth</option>
                <option value="adoption">An adoption</option>
                <option value="surrogacy">A surrogacy (commissioning parent)</option>
              </select>
            </label>
            <label style={{ flex: 1, minWidth: 160 }}>
              <span className="rf-label">{form.parentalKind === 'birth' ? 'Expected or actual birth date' : form.parentalKind === 'adoption' ? 'Placement date' : 'Birth date'}</span>
              <input type="date" className="form-input" value={form.parentalDate} min="1900-01-01" max="2100-12-31" onChange={e => set('parentalDate', e.target.value)} />
            </label>
          </div>
        )}

        {/* What it means — from the server */}
        {ready && (
          <div aria-live="polite" className="flex flex-col gap-2">
            {previewing && !p && (
              <div className="rf-box" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Loader2 size={14} className="animate-spin" /> Checking these dates…</div>
            )}
            {previewErr && <div className="rf-box rf-bad" style={{ display: 'flex', gap: 8 }}><AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} /> {previewErr}</div>}
            {p && (
              <>
                <div className="rf-box" style={{ opacity: previewing ? 0.6 : 1 }}>
                  <div className="rf-row">
                    <span>{isCustom ? 'Hours' : 'Working days'}</span>
                    <span className="rf-strong">{isCustom ? `${p.hours}h` : isHalfDay ? '½ day' : fmtDayCount(p.days)}</span>
                  </div>
                  {isCustom && p.custom?.breakMinutes > 0 && (
                    <div className="rf-row">
                      <span>Break not counted</span>
                      <span>{p.custom.startTime}–{p.custom.endTime} is {fmtHourCount(p.custom.spanMinutes / 60)}, less a {fmtBreak(p.custom.breakMinutes)} break</span>
                    </div>
                  )}
                  {p.holidays?.length > 0 && (
                    <div className="rf-row" style={{ alignItems: 'flex-start' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Flag size={12} /> Not counted</span>
                      <span style={{ textAlign: 'right' }}>{p.holidays.map(h => `${h.name} (${format(parseISO(h.date), 'EEE d MMM')})`).join(', ')}</span>
                    </div>
                  )}
                  {p.balance && (
                    <>
                      <div className="rf-row"><span>Balance now</span><span>{fmtAmount(p.balance.available, p.balance.unit)}{p.balance.pending > 0 ? ` (after ${fmtAmount(p.balance.pending, p.balance.unit)} waiting)` : ''}</span></div>
                      <div className="rf-row">
                        <span className="rf-strong">Balance after this</span>
                        <span className="rf-strong" style={{ color: p.balance.overdraw ? 'var(--bad-text)' : 'var(--ok-text)' }}>{fmtAmount(p.balance.after, p.balance.unit)}</span>
                      </div>
                    </>
                  )}
                  {p.typeAllowance && (
                    <div className="rf-row">
                      <span className="rf-strong">{p.leaveType.name} left after this</span>
                      <span className="rf-strong" style={{ color: p.typeAllowance.overdraw ? 'var(--bad-text)' : 'var(--ok-text)' }}>{fmtDayCount(p.typeAllowance.after)}</span>
                    </div>
                  )}
                  {p.parental && (
                    <div className="rf-row" style={{ alignItems: 'flex-start' }}>
                      <span>Parental leave left for this child</span>
                      <span className="rf-strong" style={{ color: p.parental.exceeds ? 'var(--bad-text)' : undefined }}>{Math.max(0, p.parental.remainingDays)} calendar days</span>
                    </div>
                  )}
                  {p.approver && !p.autoApprove && (
                    <div className="rf-row"><span>Approver</span><span>{p.approver.name}</span></div>
                  )}
                </div>

                {blocked && (
                  <div className="rf-box rf-bad" style={{ display: 'flex', gap: 8 }}><ShieldAlert size={15} style={{ flexShrink: 0, marginTop: 1 }} /> {blocked.message}</div>
                )}
                {!blocked && overdraw && (
                  <div className="rf-box rf-bad">
                    <div style={{ display: 'flex', gap: 8 }}>
                      <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                      <span>
                        {p.balance?.overdraw
                          ? `Not enough leave left: this needs ${fmtAmount(p.balance.cost, p.balance.unit)} and ${fmtAmount(p.balance.available, p.balance.unit)} ${p.balance.available === 1 ? 'is' : 'are'} available.`
                          : `Not enough ${p.leaveType.name} left in this cycle.`}
                        {!p.canOverdraw && ' Pick fewer days, or ask your approver about unpaid leave.'}
                      </span>
                    </div>
                    {p.canOverdraw && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, color: 'var(--text)', cursor: 'pointer' }}>
                        <input type="checkbox" checked={overdrawOk} onChange={e => setOverdrawOk(e.target.checked)} />
                        Book it anyway (it's recorded as booked beyond the balance)
                      </label>
                    )}
                  </div>
                )}
                {(p.rules?.warnings ?? []).concat((p.parental?.warnings ?? []).map(m => ({ message: m }))).map((w, i) => (
                  <div key={i} className="rf-box rf-warn" style={{ display: 'flex', gap: 8 }}><Info size={15} style={{ flexShrink: 0, marginTop: 1 }} /> {w.message}</div>
                ))}
                {p.clashes?.length > 0 && (
                  <div className="rf-box rf-warn">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, marginBottom: 4 }}><Users size={14} /> Also off in the team</div>
                    {p.clashes.slice(0, 5).map(c => (
                      <div key={c.requestId} className="rf-row"><span>{c.name} <span style={{ opacity: 0.75 }}>· {c.label}{c.status === 'pending' ? ' (pending)' : ''}</span></span><span>{fmtRange(c.from, c.to, { weekday: false })}</span></div>
                    ))}
                    {p.clashes.length > 5 && <div style={{ marginTop: 4 }}>+{p.clashes.length - 5} more</div>}
                  </div>
                )}
                {p.parental && <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>{p.parental.sharedNote}</p>}
                {p.document?.required && (
                  <div className="rf-box rf-warn">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}><FileText size={14} /> A sick note is needed ({p.document.reason.toLowerCase()})</div>
                    <p style={{ margin: '4px 0 8px' }}>Upload it now or later from Requests. Only HR can see it — not your manager or colleagues.</p>
                    <label className="btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12.5 }}>
                      <Upload size={14} /> {docFile ? docFile.name : 'Choose a file (PDF or photo)'}
                      <input type="file" accept="application/pdf,image/*" style={{ display: 'none' }} onChange={e => setDocFile(e.target.files?.[0] ?? null)} />
                    </label>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Note */}
        <label>
          <span className="rf-label">Note <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional{isPrivateType ? '' : ', your approver sees it'})</span></span>
          <textarea
            className="form-input resize-none"
            rows={2}
            placeholder={isPrivateType ? "Don't include medical or personal details. A sick note is uploaded separately." : 'Anything your approver should know'}
            value={form.note}
            onChange={e => set('note', e.target.value)}
          />
        </label>

        {error && <p className="rf-box rf-bad" role="alert" style={{ margin: 0 }}>{error}</p>}

        <div className="flex gap-3 pt-1">
          <button type="button" className="btn-secondary flex-1" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary flex-1" disabled={!canSubmit} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            {submitting ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : isForOther || p?.autoApprove ? 'Book leave' : 'Send for approval'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default RequestForm;